import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Container,
  HeaderBar,
  Loader,
  Toast as toast,
  TypoGraph,
} from "components";
import { getCompaniesHierarchy, setCompanyParent } from "api";
import { ICompanyIdName } from "interfaces";
import styles from "./companyHierarchyPage.module.scss";

const NONE_VALUE = "";

const CompanyHierarchyPage = () => {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingParentValue, setPendingParentValue] = useState<string>(NONE_VALUE);
  const [companies, setCompanies] = useState<ICompanyIdName[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCompaniesHierarchy();
      setCompanies(res?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /** Companies that already have a parent assigned (i.e. children). */
  const childIds = useMemo(
    () =>
      new Set(
        companies.filter((c) => c.parentCompanyId != null).map((c) => c.id)
      ),
    [companies]
  );

  /** Companies that are parents (have at least one child pointing at them). */
  const parentIds = useMemo(() => {
    const set = new Set<number>();
    companies.forEach((c) => {
      if (c.parentCompanyId != null) set.add(c.parentCompanyId);
    });
    return set;
  }, [companies]);

  /** Top-level companies grouped with their direct children. */
  const parentGroups = useMemo(() => {
    const childrenByParent = new Map<number, ICompanyIdName[]>();
    companies.forEach((c) => {
      if (c.parentCompanyId != null) {
        const list = childrenByParent.get(c.parentCompanyId) || [];
        list.push(c);
        childrenByParent.set(c.parentCompanyId, list);
      }
    });
    return companies
      .filter((c) => parentIds.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((parent) => ({
        parent,
        children: (childrenByParent.get(parent.id) || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
  }, [companies, parentIds]);

  /** Companies with neither a parent nor children — orphan, eligible to become a child. */
  const orphans = useMemo(
    () =>
      companies
        .filter((c) => c.parentCompanyId == null && !parentIds.has(c.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [companies, parentIds]
  );

  /**
   * Eligible parents for {@code rowId}:
   * - Not the row itself.
   * - Not already a child of someone else (depth-2 invariant).
   * Existing top-level parents and orphans are both valid — both have no parent of their own.
   */
  const eligibleParentsFor = (rowId: number): ICompanyIdName[] =>
    companies
      .filter((c) => c.id !== rowId && !childIds.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));

  const beginEdit = (company: ICompanyIdName) => {
    setEditingId(company.id);
    setPendingParentValue(String(company.parentCompanyId ?? NONE_VALUE));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPendingParentValue(NONE_VALUE);
  };

  const saveEdit = async (rowId: number) => {
    if (!Number.isFinite(rowId)) return;

    const parentCompanyId =
      pendingParentValue === NONE_VALUE ? null : Number(pendingParentValue);

    const current = companies.find((c) => c.id === rowId);
    const previous = current?.parentCompanyId ?? null;
    if (previous === parentCompanyId) {
      cancelEdit();
      return;
    }

    setSavingId(rowId);
    try {
      await setCompanyParent(rowId, parentCompanyId);
      toast({
        title: "Hierarchy updated",
        subTitle:
          parentCompanyId == null
            ? "Parent company cleared"
            : "Parent company updated",
      });
      cancelEdit();
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const removeFromParent = async (rowId: number) => {
    if (!Number.isFinite(rowId)) return;
    const current = companies.find((c) => c.id === rowId);
    if (!current || current.parentCompanyId == null) return;

    setSavingId(rowId);
    try {
      await setCompanyParent(rowId, null);
      toast({
        title: "Hierarchy updated",
        subTitle: `${current.name} is no longer nested`,
      });
      if (editingId === rowId) cancelEdit();
      await load();
    } finally {
      setSavingId(null);
    }
  };

  /** Inline editor rendered under the row currently being edited. */
  const renderEditPanel = (company: ICompanyIdName) => {
    if (editingId !== company.id) return null;
    const eligible = eligibleParentsFor(company.id);
    const disabled = savingId === company.id;
    return (
      <Container className={styles.editPanel}>
        <TypoGraph
          className={styles.editPanelTitle}
          variant="h4"
          content={company.name}
        />
        <Container className={styles.editPanelRow}>
          <TypoGraph variant="body2" content="Nest under" />
          <select
            className={styles.select}
            value={pendingParentValue}
            disabled={disabled}
            onChange={(e) => setPendingParentValue(e.target.value)}
          >
            <option value={NONE_VALUE}>Select a parent...</option>
            {eligible.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </Container>
        <Container className={styles.editPanelActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={cancelEdit}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => saveEdit(company.id)}
            disabled={disabled || pendingParentValue === NONE_VALUE}
          >
            Save
          </button>
        </Container>
      </Container>
    );
  };

  const renderRow = (
    company: ICompanyIdName,
    options: { canDelete: boolean }
  ) => {
    const busy = savingId === company.id;
    return (
      <Container key={`row-${company.id}`}>
        <Container className={styles.row}>
          <Container className={styles.rowName}>
            <TypoGraph variant="body1" content={company.name} />
          </Container>
          <Container className={styles.actions}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => beginEdit(company)}
              disabled={busy}
            >
              Edit
            </button>
            {options.canDelete && (
              <>
                <span className={styles.actionDivider}>|</span>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => removeFromParent(company.id)}
                  disabled={busy}
                >
                  Delete
                </button>
              </>
            )}
          </Container>
        </Container>
        {renderEditPanel(company)}
      </Container>
    );
  };

  return (
    <Container className={styles.container}>
      <HeaderBar title="Company Hierarchy" />
      <Loader loading={loading} />

      {!loading && parentGroups.length === 0 && orphans.length === 0 && (
        <TypoGraph variant="body1" content="No companies available." />
      )}

      {parentGroups.map(({ parent, children }) => (
        <Container
          key={`parent-${parent.id}`}
          className={styles.parentSection}
        >
          <Card sxContent={{ display: "grid", gridGap: "12px" }}>
            <Container className={styles.parentCard}>
              <Container className={styles.parentHeader}>
                <TypoGraph variant="h3" content={parent.name} />
              </Container>
              <Container className={styles.childrenList}>
                {children.map((child) => renderRow(child, { canDelete: true }))}
              </Container>
            </Container>
          </Card>
        </Container>
      ))}

      {orphans.length > 0 && (
        <Container className={styles.orphansSection}>
          <TypoGraph
            className={styles.sectionTitle}
            variant="h3"
            content="Top-level companies (no parent)"
          />
          {orphans.map((company) => (
            <Card
              key={`orphan-${company.id}`}
              sxContent={{ display: "grid", gridGap: "8px" }}
            >
              {renderRow(company, { canDelete: false })}
            </Card>
          ))}
        </Container>
      )}
    </Container>
  );
};

export default CompanyHierarchyPage;
